import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { IsomPart, DrawingElement } from '@/app/types/cad';
import { Upload, Search, Package, Trash2, Download, FolderOpen, Star, GripVertical } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { ScrollArea } from '@/app/components/ui/scroll-area';
import { toast } from 'sonner';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface BlockLibraryPanelProps {
  onBlockSelect: (block: IsomPart) => void;
}

const SORT_ORDER_KEY = 'isompart-library-sort-order';

// Debounce helper
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export const BlockLibraryPanel = React.memo(function BlockLibraryPanel({ onBlockSelect }: BlockLibraryPanelProps) {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [library, setLibrary] = useState<IsomPart[]>([]);
  const [sortOrder, setSortOrder] = useState<string[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const libraryInputRef = React.useRef<HTMLInputElement>(null);

  // Debounce search for better performance
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Load library from localStorage on mount ONLY
  useEffect(() => {
    const loadLibrary = () => {
      try {
        const stored = localStorage.getItem('isompart-library');
        if (stored) {
          const parsed = JSON.parse(stored);
          const libraryWithIds = parsed.map((part: IsomPart) => {
            if (!part.id) {
              return { ...part, id: `part-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` };
            }
            return part;
          });
          setLibrary(libraryWithIds);
        }

        // Load sort order
        const storedOrder = localStorage.getItem(SORT_ORDER_KEY);
        if (storedOrder) {
          setSortOrder(JSON.parse(storedOrder));
        }
      } catch (error) {
        console.error('❌ Error loading library from localStorage:', error);
      }
    };

    loadLibrary();

    const handleLibraryUpdate = () => {
      loadLibrary();
    };

    window.addEventListener('isompart-library-updated', handleLibraryUpdate);
    return () => window.removeEventListener('isompart-library-updated', handleLibraryUpdate);
  }, []);

  // Debounced save to localStorage
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem('isompart-library', JSON.stringify(library));
        
        // Update sort order to include new items
        const currentIds = library.map(b => b.id!);
        const newOrder = [
          ...sortOrder.filter(id => currentIds.includes(id)),
          ...currentIds.filter(id => !sortOrder.includes(id))
        ];
        
        if (JSON.stringify(newOrder) !== JSON.stringify(sortOrder)) {
          setSortOrder(newOrder);
          localStorage.setItem(SORT_ORDER_KEY, JSON.stringify(newOrder));
        }
      } catch (error) {
        console.error('❌ Error saving library to localStorage:', error);
      }
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [library]);

  // Save sort order with debounce
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(SORT_ORDER_KEY, JSON.stringify(sortOrder));
      } catch (error) {
        console.error('❌ Error saving sort order:', error);
      }
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [sortOrder]);

  const handleFileImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const jsonString = event.target?.result as string;
          const isomPart: IsomPart = JSON.parse(jsonString);
          
          if (!isomPart.id) {
            isomPart.id = `part-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          }
          
          setLibrary(prev => [...prev, isomPart]);
          toast.success(t('sidebar.parts.messages.added').replace('{name}', isomPart.name));
        } catch (error) {
          console.error('❌ Error loading file:', file.name, error);
          toast.error(`${t('sidebar.parts.messages.importError')}: ${file.name}`);
        }
      };
      reader.readAsText(file);
    });

    e.target.value = '';
  }, [t]);

  const handleLibraryExport = useCallback(() => {
    if (library.length === 0) {
      toast.error(t('sidebar.parts.messages.emptyLibrary'));
      return;
    }

    const filename = prompt(t('sidebar.parts.libraryExport'), 'IsomLibrary');
    if (!filename) {
      toast.info(t('sidebar.parts.messages.exportCancelled'));
      return;
    }

    const libraryData = {
      name: filename,
      version: '1.0',
      created: new Date().toISOString(),
      parts: library,
    };

    const blob = new Blob([JSON.stringify(libraryData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename.replace(/\s+/g, '_')}.isomlib`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success(t('sidebar.parts.messages.exported').replace('{count}', library.length.toString()));
  }, [library, t]);

  const handleLibraryImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const jsonString = event.target?.result as string;
        const libraryData = JSON.parse(jsonString);

        if (!libraryData.parts || !Array.isArray(libraryData.parts)) {
          toast.error(t('sidebar.parts.messages.invalidFormat'));
          return;
        }

        const currentLibrary = library;
        const newParts: IsomPart[] = [];
        
        libraryData.parts.forEach((part: IsomPart) => {
          const existsInLibrary = currentLibrary.some(libPart => 
            libPart.name === part.name && libPart.created === part.created
          );
          
          if (!existsInLibrary) {
            newParts.push(part);
          }
        });

        if (newParts.length > 0) {
          setLibrary(prev => [...prev, ...newParts]);
          toast.success(t('sidebar.parts.messages.imported').replace('{count}', newParts.length.toString()));
        } else {
          toast.info(t('sidebar.parts.messages.alreadyExists'));
        }
      } catch (error) {
        console.error('❌ Error loading library:', error);
        toast.error(t('sidebar.parts.messages.importError'));
      }
    };
    reader.readAsText(file);

    e.target.value = '';
  }, [library, t]);

  const handleBlockDelete = useCallback((blockToDelete: IsomPart) => {
    setLibrary(prev => prev.filter(block => block.id !== blockToDelete.id));
    setSortOrder(prev => prev.filter(id => id !== blockToDelete.id));
    toast.success(t('sidebar.parts.messages.deleted').replace('{name}', blockToDelete.name));
  }, [t]);

  const handleToggleFavorite = useCallback((blockToToggle: IsomPart) => {
    setLibrary(prev => prev.map(block => {
      if (block.id === blockToToggle.id) {
        const newFavoriteState = !block.favorite;
        toast.success(
          newFavoriteState 
            ? `⭐ ${block.name} zu Favoriten hinzugefügt`
            : `${block.name} von Favoriten entfernt`
        );
        return { ...block, favorite: newFavoriteState };
      }
      return block;
    }));
  }, []);

  const moveBlock = useCallback((dragId: string, hoverId: string) => {
    setSortOrder(prevOrder => {
      const newOrder = [...prevOrder];
      const dragIndex = newOrder.indexOf(dragId);
      const hoverIndex = newOrder.indexOf(hoverId);
      
      if (dragIndex === -1 || hoverIndex === -1) return prevOrder;
      
      // Remove drag item
      newOrder.splice(dragIndex, 1);
      // Insert at hover position
      newOrder.splice(hoverIndex, 0, dragId);
      
      return newOrder;
    });
  }, []);

  // Filter and sort library
  const filteredLibrary = library.filter(block => 
    block.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
  );

  // Sort by custom order, then by favorite, then alphabetically
  const sortedLibrary = [...filteredLibrary].sort((a, b) => {
    // Sort by custom sort order
    const aIndex = sortOrder.indexOf(a.id!);
    const bIndex = sortOrder.indexOf(b.id!);
    
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    
    // Then by favorite
    if (a.favorite && !b.favorite) return -1;
    if (!a.favorite && b.favorite) return 1;
    
    // Then alphabetically
    return a.name.localeCompare(b.name);
  });

  return (
    <div
      className="w-full h-full flex flex-col"
      style={{
        backgroundColor: 'var(--md-surface-container-low)',
        borderColor: 'var(--md-outline-variant)',
      }}
    >
      {/* Header */}
      <div className="p-3 border-b" style={{ borderColor: 'var(--md-outline-variant)' }}>
        <h2 className="text-base font-semibold mb-2 truncate">{t('sidebar.parts.title')}</h2>
        
        {/* Search */}
        <div className="relative mb-2">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder={t('sidebar.parts.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 text-sm"
          />
        </div>

        {/* Import Button */}
        <Button
          onClick={() => fileInputRef.current?.click()}
          variant="outline"
          className="w-full text-xs"
          size="sm"
        >
          <Upload className="w-3.5 h-3.5 mr-1.5" />
          <span className="truncate">{t('sidebar.parts.importPart')}</span>
        </Button>
        
        <input
          ref={fileInputRef}
          type="file"
          accept=".isompart"
          multiple
          onChange={handleFileImport}
          className="hidden"
        />

        {/* Library Import/Export */}
        <div className="flex gap-2 mt-2">
          <Button
            onClick={() => libraryInputRef.current?.click()}
            variant="outline"
            className="flex-1 text-xs px-2"
            size="sm"
            title={t('sidebar.parts.libraryImport')}
          >
            <FolderOpen className="w-3.5 h-3.5" />
            <span className="ml-1.5 hidden sm:inline truncate">{t('sidebar.parts.libraryImport')}</span>
          </Button>
          <Button
            onClick={handleLibraryExport}
            variant="outline"
            className="flex-1 text-xs px-2"
            size="sm"
            title={t('sidebar.parts.libraryExport')}
          >
            <Download className="w-3.5 h-3.5" />
            <span className="ml-1.5 hidden sm:inline truncate">{t('sidebar.parts.libraryExport')}</span>
          </Button>
        </div>
        
        <input
          ref={libraryInputRef}
          type="file"
          accept=".isomlib"
          onChange={handleLibraryImport}
          className="hidden"
        />
      </div>

      {/* Library List */}
      <ScrollArea className="flex-1">
        <div className="p-2">
          {sortedLibrary.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-500">
                {searchTerm 
                  ? t('sidebar.parts.notFound') 
                  : t('sidebar.parts.empty')}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {t('sidebar.parts.importHint')}
              </p>
            </div>
          ) : (
            sortedLibrary.map((block) => (
              <BlockLibraryItem
                key={block.id || `fallback-${block.name}-${block.created}`}
                block={block}
                onSelect={onBlockSelect}
                onDelete={handleBlockDelete}
                onToggleFavorite={handleToggleFavorite}
                onMove={moveBlock}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Footer Info */}
      <div
        className="p-3 border-t text-xs flex items-center justify-between"
        style={{
          backgroundColor: 'var(--md-surface)',
          borderColor: 'var(--md-outline-variant)',
          color: 'var(--md-on-surface-variant)',
        }}
      >
        <span>{t('sidebar.parts.available').replace('{count}', library.length.toString())}</span>
        {sortOrder.length > 0 && (
          <span className="flex items-center gap-1 text-xs opacity-60">
            <GripVertical className="w-3 h-3" />
            Ziehen zum Sortieren
          </span>
        )}
      </div>
    </div>
  );
});

interface BlockLibraryItemProps {
  block: IsomPart;
  onSelect: (block: IsomPart) => void;
  onDelete: (block: IsomPart) => void;
  onToggleFavorite: (block: IsomPart) => void;
  onMove: (dragId: string, hoverId: string) => void;
}

const BlockLibraryItem = React.memo(function BlockLibraryItem({ block, onSelect, onDelete, onToggleFavorite, onMove }: BlockLibraryItemProps) {
  const { t } = useLanguage();
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const [isDraggingToCanvas, setIsDraggingToCanvas] = useState(false);
  const [isDraggingToReorder, setIsDraggingToReorder] = useState(false);

  // For dragging to canvas
  const handleDragStart = useCallback((e: React.DragEvent) => {
    setIsDraggingToCanvas(true);
    e.dataTransfer.setData('application/json', JSON.stringify(block));
    e.dataTransfer.effectAllowed = 'copy';

    if (previewCanvasRef.current) {
      const dragCanvas = document.createElement('canvas');
      dragCanvas.width = 96;
      dragCanvas.height = 96;
      const dragCtx = dragCanvas.getContext('2d');
      
      if (dragCtx && previewCanvasRef.current) {
        dragCtx.drawImage(previewCanvasRef.current, 0, 0);
        dragCtx.strokeStyle = '#3b82f6';
        dragCtx.lineWidth = 2;
        dragCtx.strokeRect(0, 0, 96, 96);
        e.dataTransfer.setDragImage(dragCanvas, 48, 48);
      }
    }
  }, [block]);

  const handleDragEnd = useCallback(() => {
    setIsDraggingToCanvas(false);
  }, []);

  // For reordering in list
  const handleReorderDragStart = useCallback((e: React.DragEvent) => {
    e.stopPropagation();
    setIsDraggingToReorder(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('reorder-block-id', block.id || '');
  }, [block.id]);

  const handleReorderDragEnd = useCallback(() => {
    setIsDraggingToReorder(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    const reorderBlockId = e.dataTransfer.types.includes('reorder-block-id');
    if (reorderBlockId) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const dragId = e.dataTransfer.getData('reorder-block-id');
    if (dragId && dragId !== block.id) {
      onMove(dragId, block.id!);
    }
  }, [block.id, onMove]);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(block);
  }, [block, onDelete]);

  const handleFavoriteToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite(block);
  }, [block, onToggleFavorite]);

  const connectionCount = block.elements?.filter(el => el.type === 'connection').length || 0;

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={() => onSelect(block)}
      className={`
        relative group p-3 mb-2 rounded border cursor-move transition-all
        hover:shadow-md hover:scale-[1.02]
        ${isDraggingToCanvas || isDraggingToReorder ? 'opacity-50 scale-95' : ''}
      `}
      style={{
        backgroundColor: 'var(--md-surface)',
        borderColor: 'var(--md-outline-variant)',
      }}
    >
      {/* Drag handle for reordering */}
      <div 
        className="absolute left-1 top-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-40 transition-opacity z-10"
        draggable
        onDragStart={handleReorderDragStart}
        onDragEnd={handleReorderDragEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4" style={{ color: 'var(--md-primary)' }} />
      </div>

      {/* Delete button */}
      <button
        onClick={handleDelete}
        className="absolute top-2 right-2 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10"
        style={{
          backgroundColor: 'var(--md-error-container)',
          color: 'var(--md-on-error-container)',
        }}
        title={t('sidebar.parts.delete')}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>

      {/* Favorite button */}
      <button
        onClick={handleFavoriteToggle}
        className={`absolute top-2 left-8 p-1.5 rounded transition-opacity z-10 ${
          block.favorite ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
        style={{
          backgroundColor: block.favorite ? 'var(--md-primary-container)' : 'var(--md-surface-container)',
          color: block.favorite ? 'var(--md-on-primary-container)' : 'var(--md-on-surface-variant)',
        }}
        title={block.favorite ? 'Von Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
      >
        <Star className={`w-3.5 h-3.5 ${block.favorite ? 'fill-yellow-500 text-yellow-500' : ''}`} />
      </button>

      <div className="flex items-start gap-3 pl-5">
        {/* Thumbnail */}
        <div className="w-24 h-24 rounded border flex-shrink-0" style={{ borderColor: 'var(--md-outline-variant)' }}>
          <BlockPreview block={block} canvasRef={previewCanvasRef} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-sm truncate pr-8">{block.name}</h3>
          <div className="text-xs mt-1" style={{ color: 'var(--md-on-surface-variant)' }}>
            <div>{t('sidebar.parts.info.elements').replace('{count}', (block.elements?.length || 0).toString())}</div>
            {connectionCount > 0 && (
              <div>{t('sidebar.parts.info.sockets').replace('{count}', connectionCount.toString()).replace('{suffix}', connectionCount !== 1 ? 's' : '')}</div>
            )}
            {block.attributes && block.attributes.length > 0 && (
              <div>{t('sidebar.parts.info.attributes').replace('{count}', block.attributes.length.toString()).replace('{suffix}', block.attributes.length !== 1 ? 'e' : '')}</div>
            )}
          </div>
        </div>
      </div>

      {/* Drag hint */}
      <div className="text-xs mt-2 opacity-60" style={{ color: 'var(--md-primary)' }}>
        {t('sidebar.parts.dragToPlace')}
      </div>
    </div>
  );
});

// Block preview component
function BlockPreview({ block, canvasRef }: { block: IsomPart, canvasRef: React.RefObject<HTMLCanvasElement> }) {
  const angle = 30 * (Math.PI / 180);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const elements = block.elements || [];
    if (elements.length === 0) return;

    const isoToScreenRaw = (u: number, v: number) => {
      const x = u * Math.cos(angle) - v * Math.cos(angle);
      const y = u * Math.sin(angle) + v * Math.sin(angle);
      return { x, y };
    };

    const screenPoints: { x: number; y: number }[] = [];

    elements.forEach(el => {
      if (el.type === 'line') {
        screenPoints.push(isoToScreenRaw(el.start.u, el.start.v));
        screenPoints.push(isoToScreenRaw(el.end.u, el.end.v));
      } else if (el.type === 'circle') {
        for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 4) {
          const offsetU = el.radius * Math.cos(angle);
          const offsetV = el.radius * Math.sin(angle);
          screenPoints.push(isoToScreenRaw(el.center.u + offsetU, el.center.v + offsetV));
        }
      } else if (el.type === 'arc') {
        for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 4) {
          const offsetU = el.radius * Math.cos(angle);
          const offsetV = el.radius * Math.sin(angle);
          screenPoints.push(isoToScreenRaw(el.center.u + offsetU, el.center.v + offsetV));
        }
      } else if (el.type === 'spline' || el.type === 'shape') {
        el.points.forEach(p => screenPoints.push(isoToScreenRaw(p.u, p.v)));
      } else if (el.type === 'connection') {
        const radiusGridUnits = (el.diameter || 50) / 100;
        for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 4) {
          const offsetU = radiusGridUnits * Math.cos(angle);
          const offsetV = radiusGridUnits * Math.sin(angle);
          screenPoints.push(isoToScreenRaw(el.position.u + offsetU, el.position.v + offsetV));
        }
      }
    });

    if (screenPoints.length === 0) return;

    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    screenPoints.forEach(point => {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    });

    if (!isFinite(minX) || !isFinite(maxX) || !isFinite(minY) || !isFinite(maxY)) {
      return;
    }

    const width = maxX - minX;
    const height = maxY - minY;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    const paddingFactor = 1.3;
    const scaleX = (canvas.width / (width || 1)) / paddingFactor;
    const scaleY = (canvas.height / (height || 1)) / paddingFactor;
    const scale = Math.min(scaleX, scaleY);

    const isoToScreen = (u: number, v: number) => {
      const raw = isoToScreenRaw(u, v);
      const x = canvas.width / 2 + (raw.x - centerX) * scale;
      const y = canvas.height / 2 + (raw.y - centerY) * scale;
      return { x, y };
    };

    elements.forEach(el => {
      if (el.type === 'line') {
        const start = isoToScreen(el.start.u, el.start.v);
        const end = isoToScreen(el.end.u, el.end.v);
        ctx.strokeStyle = el.color || '#000000';
        ctx.lineWidth = Math.max(1, (el.width || 2) * scale * 0.01);
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
      } else if (el.type === 'circle') {
        // Draw circle as perfect circle in screen space (not isometric projection)
        const center = isoToScreen(el.center.u, el.center.v);
        const radiusPixels = el.radius * scale;
        
        ctx.strokeStyle = el.color || '#000000';
        ctx.lineWidth = Math.max(1, (el.width || 2) * scale * 0.01);
        ctx.lineCap = 'round';
        
        if (el.filled) {
          ctx.fillStyle = el.fillColor || el.color || '#000000';
        }
        
        ctx.beginPath();
        ctx.arc(center.x, center.y, radiusPixels, 0, 2 * Math.PI);
        
        if (el.filled) ctx.fill();
        ctx.stroke();
      } else if (el.type === 'arc') {
        // Draw arc as perfect arc in screen space (not isometric projection)
        const center = isoToScreen(el.center.u, el.center.v);
        const radiusPixels = el.radius * scale;
        
        let startAngle = el.startAngle;
        let endAngle = el.endAngle;
        let counterClockwise = el.counterClockwise || false;
        
        ctx.strokeStyle = el.color || '#000000';
        ctx.lineWidth = Math.max(1, (el.width || 2) * scale * 0.01);
        ctx.lineCap = 'round';
        
        ctx.beginPath();
        ctx.arc(center.x, center.y, radiusPixels, startAngle, endAngle, counterClockwise);
        
        ctx.stroke();
      } else if (el.type === 'shape' && el.points) {
        // Draw polygon/shape
        ctx.strokeStyle = el.strokeColor || '#000000';
        ctx.lineWidth = Math.max(1, (el.strokeWidth || 2) * scale * 0.01);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        if (el.filled) {
          ctx.fillStyle = el.fillColor || el.strokeColor || '#000000';
        }
        
        ctx.beginPath();
        el.points.forEach((p: any, idx: number) => {
          const point = isoToScreen(p.u, p.v);
          if (idx === 0) {
            ctx.moveTo(point.x, point.y);
          } else {
            ctx.lineTo(point.x, point.y);
          }
        });
        
        if (el.closed) {
          ctx.closePath();
        }
        
        if (el.filled) ctx.fill();
        ctx.stroke();
      } else if (el.type === 'spline' && el.points) {
        // Draw spline as connected line segments
        ctx.strokeStyle = el.color || '#000000';
        ctx.lineWidth = Math.max(1, (el.width || 2) * scale * 0.01);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        ctx.beginPath();
        el.points.forEach((p: any, idx: number) => {
          const point = isoToScreen(p.u, p.v);
          if (idx === 0) {
            ctx.moveTo(point.x, point.y);
          } else {
            ctx.lineTo(point.x, point.y);
          }
        });
        
        ctx.stroke();
      } else if (el.type === 'connection') {
        const pos = isoToScreen(el.position.u, el.position.v);
        const radiusPixels = ((el.diameter || 50) / 100) * scale / 2;
        ctx.strokeStyle = el.color || '#000000';
        ctx.lineWidth = Math.max(1, (el.strokeWidth || 5) * scale * 0.01);
        ctx.fillStyle = el.fillType === 'filled' ? (el.color || '#000000') : '#ffffff';
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, radiusPixels, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
      }
    });

  }, [block]);

  return (
    <canvas 
      ref={canvasRef} 
      width={96} 
      height={96}
      className="rounded"
      style={{ backgroundColor: '#ffffff' }}
    />
  );
}